import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AIAgentResponse } from '@troia-v3/types';
import { agentsApi } from '../services/agentsApi';

/**
 * Hook para buscar histórico de versões de um agente de IA
 */
export const useAgentVersions = (agentId?: string, query?: { page?: number; limit?: number }) => {
  return useQuery({
    queryKey: ['agent-versions', agentId, query],
    queryFn: () => agentsApi.getVersions(agentId!, query),
    enabled: !!agentId,
    staleTime: 30 * 1000,
  });
};

/**
 * Hook (mutation) para restaurar uma versão antiga do agente. Cria
 * uma nova versão com o systemPrompt da versão selecionada (Sprint UI-4).
 *
 * Após sucesso, invalida queries de versões e do próprio agente para
 * forçar refetch — a tab "Versões" mostra a nova versão como ativa.
 */
export const useRestoreVersion = (agentId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<AIAgentResponse, Error, { versionId: string }>({
    mutationFn: ({ versionId }: { versionId: string }) => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentsApi.restoreVersion(agentId, versionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-versions', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    },
  });
};
